import { ReactComponent as AdminSVG } from '@assets/svg/auth-tiles/admin.svg';
import { ReactComponent as DoctorSVG } from '@assets/svg/auth-tiles/doctor.svg';
import { ReactComponent as HealthWorkerSVG } from '@assets/svg/auth-tiles/health-worker.svg';
import { ReactComponent as LifestyleExpertSVG } from '@assets/svg/auth-tiles/healthstyle-expert.svg';
import { ReactComponent as PatientSVG } from '@assets/svg/auth-tiles/patient.svg';
import { ReactComponent as ProviderSVG } from '@assets/svg/auth-tiles/provider.svg';
import { Button } from '@components/ui';
import { useAppSelector } from '@hooks/app/redux';
import useTokenService from '@hooks/app/useTokenService';
import { STEPS } from '@pages/auth/registration/registration.types';
import { actions } from '@store/ducks/auth';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory } from 'react-router-dom';
import { TRole } from 'types/auth';
import { TMro } from 'types/auth-token.types';
import Header from '../../../components/header';
import RoleTermsModal from './components/role-terms-modal';
import { HighlightedTile, StepRoleSC, Tile } from './step-role.styled';

type Tile = {
  svg: JSX.Element;
  role: TRole;
  textId: string;
};

const TILES: Tile[] = [
  {
    svg: <ProviderSVG />,
    role: 'provider',
    textId: 'reg.step_3.role_provider',
  },
  {
    svg: <DoctorSVG />,
    role: 'doctor',
    textId: 'reg.step_3.role_doctor',
  },
  {
    svg: <HealthWorkerSVG />,
    role: 'medical-worker',
    textId: 'reg.step_3.role_medical_worker',
  },
  {
    svg: <LifestyleExpertSVG />,
    role: 'healthstyle-expert',
    textId: 'reg.step_3.role_healthstyle_expert',
  },
  {
    svg: <AdminSVG />,
    role: 'administration-worker',
    textId: 'reg.step_3.role_administration_worker',
  },
  {
    svg: <PatientSVG />,
    role: 'patient',
    textId: 'reg.step_3.role_patient',
  },
];

const StepRole: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { session } = useTokenService();
  const history = useHistory();

  const isLoading = useAppSelector((store) => store.auth.isLoading);

  const [showTermsModal, setShowTermsModal] = React.useState(false);
  const [selectedRole, setSelectedRole] = useState<TRole | null>(() => {
    if (!session) {
      return null;
    }

    const mros = session.decodedUserToken.mro.split(',') as TMro[];
    if (mros.includes('manager')) {
      return null;
    }

    return 'patient';
  });

  const toggleModal = React.useCallback(() => {
    setShowTermsModal((o) => !o);
  }, []);

  const onModalSubmit = React.useCallback(() => {
    if (selectedRole) {
      dispatch(actions.setRole(selectedRole));
    }
  }, [selectedRole]);

  const handleTileClick = (role: TRole) => {
    role === selectedRole ? setSelectedRole(null) : setSelectedRole(role);
  };

  const handleUpdateRole = async () => {
    if (selectedRole && selectedRole !== 'patient') {
      setShowTermsModal(true);
    } else {
      dispatch(actions.setCurrentRegStep(STEPS.SUCCESS));
    }
  };

  React.useEffect(() => {
    if (!session) {
      history.replace('/login');
    }
  }, []);

  return (
    <>
      <Prompt when={Boolean(session)} message={formatMessage({ id: 'reg.step_1.leave_message' })} />
      <div className="form-wrapper" style={{ paddingTop: 60 }}>
        <Header textId="reg.step_3.title" />

        <StepRoleSC className="right-content">
          <div className="tiles">
            {TILES.map(({ svg, role, textId }) => {
              const isHighlighted = role === 'provider';

              return isHighlighted ? (
                <HighlightedTile
                  onClick={() => handleTileClick(role as TRole)}
                  key={role}
                  selected={selectedRole === role}
                >
                  {svg}
                  <span>
                    <FormattedMessage id={textId} />
                  </span>
                </HighlightedTile>
              ) : (
                <Tile
                  onClick={() => handleTileClick(role as TRole)}
                  key={role}
                  selected={selectedRole === role}
                >
                  {svg}
                  <span>
                    <FormattedMessage id={textId} />
                  </span>
                </Tile>
              );
            })}
          </div>
          <Button className="btn" onClick={handleUpdateRole} loading={isLoading}>
            <FormattedMessage id="reg.step_3.btn" />
          </Button>
        </StepRoleSC>
      </div>
      <RoleTermsModal visible={showTermsModal} onClose={toggleModal} onSubmit={onModalSubmit} />
    </>
  );
};

export default StepRole;
